import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Download, QrCode, Calendar, MapPin, Ticket, AlertCircle, Loader } from 'lucide-react';
import { ordersAPI, ticketsAPI } from '../lib/api';
import { API } from '../lib/api';
import QRCode from 'react-qr-code';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

function useQuery() {
  const [searchParams] = useSearchParams();
  return searchParams;
}

export default function TicketConfirmationPage() {
  const query = useQuery();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { format } = useCurrency();
  const sessionId = query.get('session_id');
  const orderId = query.get('order_id');
  const [status, setStatus] = useState('checking'); // checking | success | failed
  const [order, setOrder] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const pollRef = useRef(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/explore');
      return;
    }

    const poll = async () => {
      attempts.current++;
      try {
        const resp = await ordersAPI.checkStatus(sessionId);
        const data = resp.data;
        if (data.payment_status === 'paid') {
          clearInterval(pollRef.current);
          setOrder(data.order);
          setTickets(data.tickets || []);
          setStatus('success');
        } else if (data.status === 'expired') {
          clearInterval(pollRef.current);
          setStatus('failed');
        } else if (attempts.current >= 8) {
          clearInterval(pollRef.current);
          setStatus('failed');
        }
      } catch {
        if (attempts.current >= 8) {
          clearInterval(pollRef.current);
          setStatus('failed');
        }
      }
    };

    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [sessionId, navigate]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center" data-testid="checking-payment">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-red-500 mx-auto mb-4" />
          <h2 className="text-white font-black uppercase text-xl mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Confirming Payment...</h2>
          <p className="text-gray-400 text-sm">Please wait while we verify your payment</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center" data-testid="payment-failed">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-white font-black uppercase text-2xl mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Payment Failed</h2>
          <p className="text-gray-400 mb-6">Your payment could not be processed. Please try again.</p>
          <Link to="/explore" className="btn-primary px-6 py-3 rounded font-bold text-sm inline-block">
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-16 page-enter" data-testid="ticket-confirmation-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-3xl font-black uppercase text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            You're In!
          </h1>
          <p className="text-gray-400">Your tickets have been confirmed. Check your email for a copy.</p>
          {order && (
            <p className="text-gray-500 text-xs mt-1">Order ID: <span className="text-yellow-400 font-mono">{order.order_id?.slice(0, 8).toUpperCase()}</span></p>
          )}
        </div>

        {/* Tickets */}
        <div className="space-y-4" data-testid="tickets-list">
          {tickets.map((ticket, idx) => (
            <div key={ticket.ticket_id} className="ticket-card p-6" data-testid={`ticket-${ticket.ticket_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <p className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-1">Your Ticket #{idx + 1}</p>
                  <h3 className="text-white font-black uppercase text-lg leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {ticket.event_title}
                  </h3>
                </div>
                <Ticket className="text-red-500 flex-shrink-0" size={24} />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Calendar size={14} className="text-red-500 flex-shrink-0" />
                  <span>{ticket.event_date} {ticket.event_time && `• ${ticket.event_time}`}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <MapPin size={14} className="text-red-500 flex-shrink-0" />
                  <span className="truncate">{ticket.event_location}</span>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500 mb-4 py-3 border-t border-gray-800">
                <span>Type: <span className="text-white font-medium">{ticket.ticket_type_name}</span></span>
                <span>Holder: <span className="text-white font-medium">{ticket.holder_name}</span></span>
                <span className="font-mono text-yellow-400">#{ticket.ticket_id.slice(0, 8).toUpperCase()}</span>
              </div>

              {/* QR Code */}
              {showQR && selectedTicket === ticket.ticket_id ? (
                <div className="flex flex-col items-center py-4">
                  <div className="qr-container">
                    <QRCode value={ticket.qr_data} size={200} />
                  </div>
                  <p className="text-gray-500 text-xs mt-2">Show this code at the entrance</p>
                  <button
                    onClick={() => { setShowQR(false); setSelectedTicket(null); }}
                    className="mt-3 text-xs text-gray-500 hover:text-white transition-colors"
                  >
                    Hide QR
                  </button>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => { setSelectedTicket(ticket.ticket_id); setShowQR(true); }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-700 rounded text-xs font-bold uppercase text-gray-300 hover:border-red-500 hover:text-white transition-colors"
                    data-testid={`view-qr-${ticket.ticket_id}`}
                  >
                    <QrCode size={14} /> {t('viewQR')}
                  </button>
                  <a
                    href={`${API}/tickets/${ticket.ticket_id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 btn-primary rounded text-xs font-bold uppercase"
                    data-testid={`download-pdf-${ticket.ticket_id}`}
                  >
                    <Download size={14} /> {t('downloadPDF')}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 text-center space-y-3">
          <Link to="/my-tickets" className="block w-full py-3 border border-gray-700 rounded font-bold uppercase text-sm text-gray-300 hover:border-red-500 hover:text-white transition-colors">
            View All My Tickets
          </Link>
          <Link to="/explore" className="block w-full btn-primary py-3 rounded font-bold uppercase text-sm">
            Explore More Events
          </Link>
        </div>
      </div>
    </div>
  );
}
